import { useCallback, useEffect, useRef, useState } from "react";
import useEvent from "react-use-event-hook";

import { MAPLIBRE_HOVER_POPUP_CLOSE_DELAY_MS } from "./maplibre-marker.constants";

export interface IMapLibreHoverPopup {
  /** Id of the marker whose popup is open, or null. Only ever one — as Leaflet allowed per map. */
  openId: number | null;
  open: (id: number) => void;
  close: (id?: number) => void;
  /** Start the grace period. Cancelled by `cancelScheduledClose`, e.g. when the popup is entered. */
  scheduleClose: (id: number) => void;
  cancelScheduledClose: () => void;
}

/**
 * Hover-driven map popups: open on marker hover, stay open while the pointer moves onto the popup itself,
 * close shortly after it leaves either.
 *
 * The MapLibre replacement for the twin `usePassengerMapPopUp` / `useStationsMapPopup` Leaflet hooks. Those
 * had to hold a ref to every marker and call `openPopup()` / `closePopup()` imperatively, and hand-attach
 * mouseenter/mouseleave to the popup's DOM node after Leaflet created it. Here the popup is just React, so
 * only "which id is open" has to be tracked and the listeners are ordinary JSX props.
 */
export const useMapLibreHoverPopup = (): IMapLibreHoverPopup => {
  const [openId, setOpenId] = useState<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const cancelScheduledClose = useCallback((): void => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const open = useEvent((id: number): void => {
    cancelScheduledClose();
    setOpenId(id);
  });

  const close = useEvent((id?: number): void => {
    cancelScheduledClose();
    // An id closes only its own popup: a marker leaving late must not shut a popup another marker has since
    // opened. Omitting it closes whatever is open.
    setOpenId(current => (id === undefined || current === id ? null : current));
  });

  const scheduleClose = useEvent((id: number): void => {
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(
      () => close(id),
      MAPLIBRE_HOVER_POPUP_CLOSE_DELAY_MS,
    );
  });

  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  /*
   * Click-away, which Leaflet got from a MUI `ClickAwayListener` wrapped around every marker. A document
   * listener covers clicks anywhere — the map, the grid beside it, the page chrome — where MapLibre's own
   * `closeOnClick` would only catch the map itself.
   *
   * Clicks inside the popup are ignored so its own controls keep working, and clicks on a marker are left
   * to the marker's handler, which decides whether to open a different one. The listener is only attached
   * while something is open, and passive effects run after the opening click has finished dispatching, so
   * it cannot close the popup it just opened.
   */
  useEffect(() => {
    if (openId === null) {
      return;
    }

    const handleDocumentClick = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;

      if (
        target?.closest?.(".maplibregl-popup") ||
        target?.closest?.(".maplibregl-marker")
      ) {
        return;
      }

      close();
    };

    document.addEventListener("click", handleDocumentClick);

    return () => document.removeEventListener("click", handleDocumentClick);
  }, [openId, close]);

  return { openId, open, close, scheduleClose, cancelScheduledClose };
};
